import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import {
  Plus, Trash2, Loader2, DollarSign, ShoppingBag, Receipt, Vault,
  Wallet, Calculator, CheckCircle2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  getAccountingDaily, setOpeningCash, setActualCash as saveActualCashApi, getOtherIncome, createOtherIncome, deleteOtherIncome,
  getExpenses, createExpense, deleteExpense, getSafeDrops, createSafeDrop, deleteSafeDrop,
  getDailyEarnings, dailySettle, deleteDailyPayout, getShopConfig,
} from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function AccountingBoard() {
  const { selectedBranch, branches, selectedDate } = useOutletContext();
  const branchId = selectedBranch || branches[0]?.id;
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const [metrics, setMetrics] = useState(null);
  const [otherIncome, setOtherIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [safeDrops, setSafeDrops] = useState([]);
  const [payoutMatrix, setPayoutMatrix] = useState([]);
  const [shopCfg, setShopCfg] = useState(null);
  const [payoutInputs, setPayoutInputs] = useState({}); // staff_id -> { cash, hold }
  const [loading, setLoading] = useState(false);

  const [openingInput, setOpeningInput] = useState("");
  const [actualCash, setActualCash] = useState("");
  const [actualCashNote, setActualCashNote] = useState("");
  const [savingActual, setSavingActual] = useState(false);
  const [dialog, setDialog] = useState(null); // 'income' | 'expense' | 'safe'
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [m, oi, ex, sd, pm, cfg] = await Promise.all([
        getAccountingDaily(dateStr, branchId),
        getOtherIncome({ date: dateStr, branch_id: branchId }),
        getExpenses({ date: dateStr, branch_id: branchId }),
        getSafeDrops({ date: dateStr, branch_id: branchId }),
        getDailyEarnings(dateStr, branchId, false),
        getShopConfig(),
      ]);
      setMetrics(m);
      setOpeningInput(String(m.opening_cash ?? 0));
      setActualCash(m.actual_cash_counted != null ? String(m.actual_cash_counted) : "");
      setActualCashNote(m.actual_cash_note || "");
      setOtherIncome(oi);
      setExpenses(ex);
      setSafeDrops(sd);
      setPayoutMatrix(pm);
      setShopCfg(cfg);
      // Seed split inputs from existing payouts (cash already paid / held)
      const seed = {};
      pm.forEach((p) => {
        if (p.settled) seed[p.staff_id] = { cash: String(p.cash_paid ?? 0), hold: String(p.transfer_held ?? 0), note: p.note ?? "" };
      });
      setPayoutInputs(seed);
    } catch {
      toast.error("Failed to load accounting data");
    } finally {
      setLoading(false);
    }
  }, [branchId, dateStr]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveOpening = async () => {
    try {
      await setOpeningCash({ date: dateStr, branch_id: branchId, amount: parseFloat(openingInput) || 0 });
      toast.success("Opening cash saved");
      fetchAll();
    } catch { toast.error("Failed to save opening cash"); }
  };

  const saveActual = async () => {
    setSavingActual(true);
    try {
      const res = await saveActualCashApi({ date: dateStr, branch_id: branchId, amount: parseFloat(actualCash) || 0, note: actualCashNote });
      setActualCash(res.actual_cash_counted != null ? String(res.actual_cash_counted) : "");
      setActualCashNote(res.actual_cash_note || "");
      toast.success("Actual cash saved — tomorrow's opening float will roll from this");
      fetchAll();
    } catch { toast.error("Failed to save actual cash"); }
    finally { setSavingActual(false); }
  };

  // Split-payout input handlers (auto-balance against gross)
  const setPayoutCash = (p, val) => {
    const cash = Math.max(0, Math.min(parseFloat(val) || 0, p.gross));
    setPayoutInputs((prev) => ({ ...prev, [p.staff_id]: { ...prev[p.staff_id], cash: val, hold: String(Math.round((p.gross - cash) * 100) / 100) } }));
  };
  const setPayoutHold = (p, val) => {
    const hold = Math.max(0, Math.min(parseFloat(val) || 0, p.gross));
    setPayoutInputs((prev) => ({ ...prev, [p.staff_id]: { ...prev[p.staff_id], hold: val, cash: String(Math.round((p.gross - hold) * 100) / 100) } }));
  };
  const setPayoutNote = (p, val) => {
    setPayoutInputs((prev) => ({ ...prev, [p.staff_id]: { ...prev[p.staff_id], note: val } }));
  };

  const handleExecutePayout = async (p) => {
    const inp = payoutInputs[p.staff_id] || {};
    const cash = parseFloat(inp.cash) || 0;
    const hold = inp.hold !== undefined && inp.hold !== "" ? parseFloat(inp.hold) || 0 : Math.round((p.gross - cash) * 100) / 100;
    setConfirmingId(p.staff_id);
    try {
      await dailySettle({ staff_id: p.staff_id, date: dateStr, branch_id: branchId, cash_paid: cash, transfer_held: hold, note: inp.note || "" });
      toast.success(`Payout logged — $${cash.toFixed(2)} cash, $${hold.toFixed(2)} held`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to settle");
    } finally { setConfirmingId(null); }
  };

  const handleReverse = async (payoutId) => {
    try {
      await deleteDailyPayout(payoutId);
      toast.success("Payout reversed");
      fetchAll();
    } catch { toast.error("Failed to reverse"); }
  };

  const expected = metrics?.expected_cash ?? 0;
  const variance = actualCash === "" ? null : Math.round((parseFloat(actualCash || 0) - expected) * 100) / 100;

  if (loading && !metrics) {
    return <div className="flex items-center justify-center h-80"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>;
  }

  return (
    <div className="animate-in space-y-5" data-testid="accounting-board">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={DollarSign} label="Booking Revenue" value={money(metrics?.booking_revenue)} testid="metric-booking"
          sub={[
            { label: "Cash", value: money(metrics?.cash_bookings) },
            { label: "Eftpos", value: money(metrics?.eftpos_bookings) },
          ]} />
        <Metric icon={ShoppingBag} label="Retail Revenue" value={money(metrics?.retail_revenue)} testid="metric-retail"
          sub={[
            { label: "Cash", value: money(metrics?.cash_retail) },
            { label: "Eftpos", value: money(metrics?.transfer_retail) },
          ]} />
        <Metric icon={Receipt} label="Total Expenses" value={money(metrics?.total_expenses)} accent="terracotta" testid="metric-expenses" />
        <Metric icon={Wallet} label="Expected Cash in Drawer" value={money(expected)} accent="forest-solid" testid="metric-expected-cash" highlight />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT: ledgers */}
        <div className="space-y-4">
          {/* Opening cash */}
          {(!shopCfg || shopCfg.carry_over_opening_cash_enabled) && (
          <div className="bg-white border border-border rounded-xl p-4">
            <Label className="text-sm font-semibold text-forest">Opening Cash</Label>
            <div className="flex gap-2 mt-2">
              <Input type="number" value={openingInput} onChange={(e) => setOpeningInput(e.target.value)} className="h-9" data-testid="opening-cash-input" />
              <Button onClick={saveOpening} variant="outline" data-testid="save-opening-btn">Save</Button>
            </div>
          </div>
          )}

          {(!shopCfg || shopCfg.other_income_enabled) && (
          <LedgerCard
            title="Other Income" icon={ShoppingBag} testid="other-income"
            onAdd={() => setDialog("income")} addLabel="Add Other Income"
            rows={otherIncome}
            renderRow={(r) => ({ left: r.item_name, mid: r.payment_method === "Transfer" ? "Eftpos" : r.payment_method, right: money(r.amount) })}
            onDelete={async (id) => { await deleteOtherIncome(id); fetchAll(); }}
            empty="No retail sales logged."
          />
          )}

          {(!shopCfg || shopCfg.expenses_enabled) && (
          <LedgerCard
            title="Daily Expenses(Cash)" icon={Receipt} testid="expense"
            onAdd={() => setDialog("expense")} addLabel="Add Expense"
            rows={expenses}
            renderRow={(r) => ({ left: r.description, mid: "", right: money(r.amount) })}
            onDelete={async (id) => { await deleteExpense(id); fetchAll(); }}
            empty="No expenses logged."
          />
          )}

          {(!shopCfg || shopCfg.safe_drop_enabled) && (
          <LedgerCard
            title="Safe cash" icon={Vault} testid="safe-drop"
            onAdd={() => setDialog("safe")} addLabel="Put into Safe"
            rows={safeDrops}
            renderRow={(r) => ({ left: r.notes || "Safe drop", mid: "", right: money(r.amount) })}
            onDelete={async (id) => { await deleteSafeDrop(id); fetchAll(); }}
            empty="No safe drops logged."
          />
          )}
        </div>

        {/* RIGHT: payout matrix + EOD */}
        <div className="space-y-4">
          {(!shopCfg || shopCfg.daily_payout_enabled) && (
          <div className="bg-white border border-border rounded-xl p-4" data-testid="cash-payout-matrix">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="h-4 w-4 text-forest" />
              <h3 className="font-semibold text-forest">Daily Staff Cash Payout</h3>
            </div>
            {payoutMatrix.length === 0 ? (
              <p className="text-sm text-sage/70">No therapists active today.</p>
            ) : (
              <div className="space-y-3">
                {payoutMatrix.map((p) => {
                  const inp = payoutInputs[p.staff_id] || { cash: "", hold: "" };
                  return (
                    <div key={p.staff_id} className="border border-border rounded-lg px-3 py-2.5" data-testid={`payout-row-${p.staff_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <p className="font-medium text-forest truncate">{p.staff_name}</p>
                          <p className="text-[11px] text-sage">
                            {p.shift_hours}h · comm {money(p.commission)}
                            {p.top_up > 0 && <span className="text-ochre"> · top-up {money(p.top_up)}</span>}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] text-sage">Total Owed</p>
                          <p className="font-bold text-forest" data-testid={`payout-owed-${p.staff_id}`}>{money(p.gross)}</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-sage/80 mb-2" data-testid={`payout-sales-${p.staff_id}`}>
                        Sales: {money(p.total_sales)} (Cash {money(p.cash_sales)} | Eftpos {money(p.Eftpos_sales)})
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-sage">Pay Cash Now ($)</Label>
                          <Input type="number" min="0" step="0.01" value={inp.cash ?? ""}
                            onChange={(e) => setPayoutCash(p, e.target.value)}
                            className="h-8 text-sm" data-testid={`payout-cash-${p.staff_id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-sage">transfer</Label>
                          <Input type="number" min="0" step="0.01" value={inp.hold ?? ""}
                            onChange={(e) => setPayoutHold(p, e.target.value)}
                            className="h-8 text-sm" data-testid={`payout-hold-${p.staff_id}`} />
                        </div>
                      </div>
                      <div className="flex items-end justify-between mt-2 gap-2">
                        <div className="min-w-0 flex-1">
                          {p.settled ? (
                            <span className="text-[11px] text-forest flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Logged: {money(p.cash_paid)} cash · {money(p.transfer_held)} held
                            </span>
                          ) : <span className="text-[11px] text-sage/60">Not yet settled</span>}
                          <Label className="text-[10px] text-sage block mt-1">Note</Label>
                          <Input
                            value={inp.note ?? ""}
                            onChange={(e) => setPayoutNote(p, e.target.value)}
                            placeholder="Optional note"
                            className="h-7 text-xs mt-0.5"
                            data-testid={`payout-note-${p.staff_id}`}
                          />
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {p.settled && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-sage" onClick={() => handleReverse(p.payout_id)} data-testid={`reverse-${p.staff_id}`}>
                              <RotateCcw className="h-3 w-3 mr-1" /> Reverse
                            </Button>
                          )}
                          <Button size="sm" className="h-7 text-xs bg-forest hover:bg-forest-light" onClick={() => handleExecutePayout(p)} disabled={confirmingId === p.staff_id} data-testid={`execute-payout-${p.staff_id}`}>
                            {confirmingId === p.staff_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Payment"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* EOD discrepancy */}
          <div className="bg-white border border-border rounded-xl p-4" data-testid="eod-discrepancy">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-4 w-4 text-forest" />
              <h3 className="font-semibold text-forest">End of Day Cash Check</h3>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-sage">Expected Cash in Drawer</span>
              <span className="font-bold text-forest" data-testid="eod-expected">{money(expected)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <Label className="text-sm text-sage">Actual Cash Counted</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={actualCash} onChange={(e) => setActualCash(e.target.value)} placeholder="0.00" className="h-9 w-32 text-right" data-testid="actual-cash-input" />
                <Button size="sm" variant="outline" onClick={saveActual} disabled={savingActual} data-testid="save-actual-cash-btn">
                  {savingActual ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            </div>
            {variance !== null && (
              <div className={`rounded-lg p-3 text-center font-bold ${variance === 0 ? "bg-forest/10 text-forest" : "bg-red-50 text-red-600"}`} data-testid="discrepancy-result">
                {variance === 0
                  ? "Cash Variance: $0.00 — Balanced ✓"
                  : variance > 0
                    ? `Cash Variance: +${money(variance)} (Over)`
                    : `Cash Variance: -${money(Math.abs(variance))} (Short)`}
              </div>
            )}
            <div className="mt-3">
              <Label className="text-sm text-sage">Note</Label>
              <Input
                value={actualCashNote}
                onChange={(e) => setActualCashNote(e.target.value)}
                placeholder="Optional note for this cash count (saved on Save)"
                className="h-9 mt-1"
                data-testid="actual-cash-note-input"
              />
            </div>
          </div>
        </div>
      </div>

      <AddEntryDialog
        type={dialog}
        onClose={() => setDialog(null)}
        onSaved={() => { setDialog(null); fetchAll(); }}
        date={dateStr}
        branchId={branchId}
      />
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, highlight, testid, sub }) {
  return (
    <div className={`rounded-xl p-4 border ${highlight ? "bg-forest text-white border-forest" : "bg-white border-border"}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${highlight ? "text-white/80" : "text-sage"}`} />
        <span className={`text-xs ${highlight ? "text-white/80" : "text-sage"}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${highlight ? "text-white" : accent === "terracotta" ? "text-terracotta" : "text-forest"}`}>{value}</p>
      {sub && sub.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/60 space-y-0.5" data-testid={`${testid}-breakdown`}>
          {sub.map((s) => (
            <div key={s.label} className="flex items-center justify-between text-[11px]">
              <span className="text-sage">{s.label}</span>
              <span className="font-medium text-forest">{s.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LedgerCard({ title, icon: Icon, onAdd, addLabel, rows, renderRow, onDelete, empty, testid }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-forest" />
          <h3 className="font-semibold text-forest">{title}</h3>
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${testid}-btn`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> {addLabel}
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-sage/60">{empty}</p>
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => {
            const cell = renderRow(r);
            return (
              <div key={r.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`${testid}-row-${r.id}`}>
                <span className="text-forest truncate flex-1">{cell.left}</span>
                {cell.mid && <span className="text-xs text-sage mx-2">{cell.mid}</span>}
                <span className="font-medium text-forest mr-2">{cell.right}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => onDelete(r.id)} data-testid={`del-${testid}-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddEntryDialog({ type, onClose, onSaved, date, branchId }) {
  const [form, setForm] = useState({ name: "", amount: "", payment_method: "Cash", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ name: "", amount: "", payment_method: "Cash", notes: "" }); }, [type]);

  const titles = { income: "Add Other Income", expense: "Add Expense", safe: "Put Cash into Safe" };

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      if (type === "income") {
        if (!form.name) { toast.error("Enter item name"); setSaving(false); return; }
        await createOtherIncome({ date, branch_id: branchId, item_name: form.name, amount: amt, payment_method: form.payment_method });
      } else if (type === "expense") {
        if (!form.name) { toast.error("Enter expense name"); setSaving(false); return; }
        await createExpense({ date, branch_id: branchId, description: form.name, amount: amt });
      } else if (type === "safe") {
        await createSafeDrop({ date, branch_id: branchId, amount: amt, notes: form.notes });
      }
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!type} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="add-entry-dialog">
        <DialogHeader><DialogTitle className="text-forest">{titles[type]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {type === "income" && (
            <div className="space-y-1">
              <Label className="text-sm">Item Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Essential Oil, Balm, Water…" data-testid="entry-name" />
            </div>
          )}
          {type === "expense" && (
            <div className="space-y-1">
              <Label className="text-sm">Expense Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Laundry, Supplies…" data-testid="entry-name" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-sm">Amount ($)</Label>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="entry-amount" />
          </div>
          {type === "income" && (
            <div className="space-y-1">
              <Label className="text-sm">Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm((p) => ({ ...p, payment_method: v }))}>
                <SelectTrigger data-testid="entry-payment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Transfer">Eftpos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {type === "safe" && (
            <div className="space-y-1">
              <Label className="text-sm">Note (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="e.g. mid-day drop" data-testid="entry-notes" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-forest hover:bg-forest-light" data-testid="entry-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
